"use client";

import { useEffect, useRef } from "react";

type Variant = "luxury" | "dust" | "mist" | "petals";

interface AmbientCanvasProps {
  variant?: Variant;
  className?: string;
  accent?: string;
  density?: number;
}

interface Particle {
  x: number;
  y: number;
  size: number;
  alpha: number;
  baseAlpha: number;
  vx: number;
  vy: number;
  phase: number;
  kind: string;
  r: number;
  g: number;
  b: number;
  rot: number;
  vrot: number;
}

function hexToRgb(hex: string) {
  const v = hex.replace("#", "");
  const n = parseInt(
    v.length === 3 ? v.split("").map((c) => c + c).join("") : v,
    16
  );
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export default function AmbientCanvas({
  variant = "dust",
  className = "",
  accent = "#c9a84c",
  density = 1,
}: AmbientCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let raf = 0;
    let running = true;
    let w = 0;
    let h = 0;
    let particles: Particle[] = [];
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const accentRgb = hexToRgb(accent);

    const configs: Record<Variant, Record<string, number>> = {
      luxury: { dust: Math.round(90 * density), bokeh: 10 },
      dust: { dust: Math.round(64 * density), bokeh: 5 },
      mist: { mist: Math.round(16 * density) },
      petals: { petals: Math.round(20 * density) },
    };

    const init = () => {
      particles = [];
      const cfg = configs[variant];

      for (let i = 0; i < (cfg.dust || 0); i++) {
        particles.push({
          x: Math.random() * w,
          y: Math.random() * h,
          size: 0.6 + Math.random() * 1.9,
          alpha: 0.12 + Math.random() * 0.38,
          baseAlpha: 0.12 + Math.random() * 0.38,
          vx: (Math.random() - 0.5) * 0.12,
          vy: -(0.05 + Math.random() * 0.25),
          phase: Math.random() * Math.PI * 2,
          kind: "dust",
          r: accentRgb.r,
          g: accentRgb.g,
          b: accentRgb.b,
          rot: 0,
          vrot: 0,
        });
      }

      for (let i = 0; i < (cfg.bokeh || 0); i++) {
        particles.push({
          x: Math.random() * w,
          y: Math.random() * h,
          size: 40 + Math.random() * 90,
          alpha: 0.03 + Math.random() * 0.05,
          baseAlpha: 0.03 + Math.random() * 0.05,
          vx: (Math.random() - 0.5) * 0.06,
          vy: (Math.random() - 0.5) * 0.05,
          phase: Math.random() * Math.PI * 2,
          kind: "bokeh",
          r: accentRgb.r,
          g: accentRgb.g,
          b: accentRgb.b,
          rot: 0,
          vrot: 0,
        });
      }

      for (let i = 0; i < (cfg.mist || 0); i++) {
        particles.push({
          x: Math.random() * w,
          y: h * (0.5 + Math.random() * 0.5),
          size: 24 + Math.random() * 46,
          alpha: 0,
          baseAlpha: 0.12 + Math.random() * 0.16,
          vx: (Math.random() - 0.5) * 0.22,
          vy: -(0.35 + Math.random() * 0.85),
          phase: Math.random() * Math.PI * 2,
          kind: "mist",
          r: accentRgb.r,
          g: accentRgb.g,
          b: accentRgb.b,
          rot: 0,
          vrot: 0,
        });
      }

      for (let i = 0; i < (cfg.petals || 0); i++) {
        particles.push({
          x: Math.random() * w,
          y: h * (0.4 + Math.random() * 0.6),
          size: 4 + Math.random() * 8,
          alpha: 0.2 + Math.random() * 0.3,
          baseAlpha: 0.2 + Math.random() * 0.3,
          vx: (Math.random() - 0.5) * 0.5,
          vy: -(0.3 + Math.random() * 0.7),
          phase: Math.random() * Math.PI * 2,
          kind: "petal",
          r: accentRgb.r,
          g: accentRgb.g,
          b: accentRgb.b,
          rot: (Math.random() - 0.5) * 180,
          vrot: (Math.random() - 0.5) * 3,
        });
      }
    };

    const draw = () => {
      if (!running) return;
      ctx!.clearRect(0, 0, w, h);

      for (const p of particles) {
        p.phase += 0.005;

        if (p.kind === "dust") {
          p.x += p.vx;
          p.y += p.vy;
          if (p.y < -12) p.y = h + 12;
          if (p.x < -12) p.x = w + 12;
          if (p.x > w + 12) p.x = -12;
          const twinkle = p.alpha * (0.6 + 0.4 * Math.sin(p.phase * 3));
          ctx!.beginPath();
          ctx!.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx!.fillStyle = `rgba(${p.r},${p.g},${p.b},${twinkle})`;
          ctx!.fill();
        } else if (p.kind === "bokeh") {
          p.x += p.vx;
          p.y += p.vy;
          if (p.x < -p.size) p.x = w + p.size;
          if (p.x > w + p.size) p.x = -p.size;
          if (p.y < -p.size) p.y = h + p.size;
          if (p.y > h + p.size) p.y = -p.size;
          const a = p.alpha * (0.6 + 0.4 * Math.sin(p.phase));
          const g = ctx!.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size);
          g.addColorStop(0, `rgba(${p.r},${p.g},${p.b},${a})`);
          g.addColorStop(1, `rgba(${p.r},${p.g},${p.b},0)`);
          ctx!.fillStyle = g;
          ctx!.beginPath();
          ctx!.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx!.fill();
        } else if (p.kind === "mist") {
          p.x += p.vx;
          p.y += p.vy;
          p.size += 0.02;
          if (p.y < h * 0.3 || p.y > h + 60) {
            p.y = h * (0.7 + Math.random() * 0.3);
            p.x = Math.random() * w;
            p.size = 24 + Math.random() * 46;
          }
          const a = p.baseAlpha * Math.sin((p.y / h) * Math.PI);
          const g = ctx!.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size);
          g.addColorStop(0, `rgba(${p.r},${p.g},${p.b},${Math.max(0, a)})`);
          g.addColorStop(1, `rgba(${p.r},${p.g},${p.b},0)`);
          ctx!.fillStyle = g;
          ctx!.beginPath();
          ctx!.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx!.fill();
        } else if (p.kind === "petal") {
          p.x += p.vx + Math.sin(p.phase) * 0.4;
          p.y += p.vy;
          p.rot += p.vrot;
          if (p.y < -24) {
            p.y = h + 24;
            p.x = Math.random() * w;
          }
          ctx!.save();
          ctx!.translate(p.x, p.y);
          ctx!.rotate((p.rot * Math.PI) / 180);
          ctx!.fillStyle = `rgba(${p.r},${p.g},${p.b},${
            p.alpha * (0.6 + 0.4 * Math.sin(p.phase * 2))
          })`;
          ctx!.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
          ctx!.restore();
        }
      }

      raf = requestAnimationFrame(draw);
    };

    const resize = () => {
      const rect = canvas.parentElement?.getBoundingClientRect();
      w = rect?.width || canvas.offsetWidth || window.innerWidth;
      h = rect?.height || canvas.offsetHeight || window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      init();
    };

    resize();
    draw();

    const onResize = () => resize();
    window.addEventListener("resize", onResize);

    const io = new IntersectionObserver(
      (entries) => {
        running = entries[0].isIntersecting;
        if (running) {
          raf = requestAnimationFrame(draw);
        } else {
          cancelAnimationFrame(raf);
        }
      },
      { threshold: 0.02 }
    );
    io.observe(canvas);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      io.disconnect();
    };
  }, [variant, accent, density]);

  return (
    <canvas
      ref={canvasRef}
      className={`pointer-events-none absolute inset-0 ${className}`}
      aria-hidden="true"
    />
  );
}